import { Chart, ChartData, ChartOptions } from 'chart.js';
import { 
  LineController, 
  LineElement, 
  PointElement, 
  LinearScale, 
  CategoryScale,
  BarController,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// Register Chart.js components
Chart.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  BarController,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Color constants migrated from AngularJS
export const rsColors = {
  red: '#FF4444',
  salmon: '#FF6B6B',
  purple: '#6C5CE7',
  darkGreen: '#00B894',
  jet: '#2D3436',
  redDash: '#FF4444',
  purpleDash: '#6C5CE7',
  plumDash: '#A29BFE',
  rbEnd: '#00B894',
  greenBorder: '#00B894',
  offWhite: '#F8F9FA',
  blueDash: '#74B9FF'
};

// Chart configuration types
export interface ChartConfig {
  type: 'line' | 'bar';
  data: ChartData;
  options: ChartOptions;
}

export interface ChartDataPoint {
  date: string;
  value: number;
  [key: string]: any;
}

export interface ChartSeries {
  label: string;
  data: ChartDataPoint[];
  color: string;
  fill?: boolean;
}

// Chart service class
class ChartService {
  private charts = new Map<string, Chart>();

  // Create a line chart
  createLineChart(
    canvasId: string,
    series: ChartSeries[],
    options: Partial<ChartOptions> = {}
  ): Chart {
    const ctx = document.getElementById(canvasId) as HTMLCanvasElement;
    if (!ctx) {
      throw new Error(`Canvas element with id '${canvasId}' not found`);
    }

    // Destroy existing chart if it exists
    if (this.charts.has(canvasId)) {
      this.charts.get(canvasId)?.destroy();
    }

    const chartData: ChartData = {
      labels: series[0]?.data.map(point => point.date) || [],
      datasets: series.map(s => ({
        label: s.label,
        data: s.data.map(point => point.value),
        borderColor: s.color,
        backgroundColor: s.fill ? `${s.color}20` : 'transparent',
        fill: s.fill || false,
        tension: 0.4,
        pointRadius: 3,
        pointHoverRadius: 6
      }))
    };

    const defaultOptions: ChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'top' as const
        },
        tooltip: {
          mode: 'index' as const,
          intersect: false,
          callbacks: {
            label: (context) => {
              return `${context.dataset.label}: ${context.parsed.y}`;
            }
          }
        }
      },
      scales: {
        x: {
          type: 'category' as const,
          display: true,
          title: {
            display: true,
            text: 'Date'
          }
        },
        y: {
          type: 'linear' as const,
          display: true,
          title: {
            display: true,
            text: 'Value'
          }
        }
      },
      interaction: {
        mode: 'nearest' as const,
        axis: 'x' as const,
        intersect: false
      }
    };

    const chart = new Chart(ctx, {
      type: 'line',
      data: chartData,
      options: { ...defaultOptions, ...options }
    });

    this.charts.set(canvasId, chart);
    return chart;
  }

  // Create a bar chart
  createBarChart(
    canvasId: string,
    series: ChartSeries[],
    options: Partial<ChartOptions> = {}
  ): Chart {
    const ctx = document.getElementById(canvasId) as HTMLCanvasElement;
    if (!ctx) {
      throw new Error(`Canvas element with id '${canvasId}' not found`);
    }

    // Destroy existing chart if it exists
    if (this.charts.has(canvasId)) {
      this.charts.get(canvasId)?.destroy();
    }

    const chartData: ChartData = {
      labels: series[0]?.data.map(point => point.date) || [],
      datasets: series.map(s => ({
        label: s.label,
        data: s.data.map(point => point.value),
        backgroundColor: s.color,
        borderColor: s.color,
        borderWidth: 1
      }))
    };

    const defaultOptions: ChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'top' as const
        },
        tooltip: {
          mode: 'index' as const,
          intersect: false
        }
      },
      scales: {
        x: {
          type: 'category' as const,
          display: true,
          title: {
            display: true,
            text: 'Date'
          }
        },
        y: {
          type: 'linear' as const,
          display: true,
          title: {
            display: true,
            text: 'Value'
          }
        }
      }
    };

    const chart = new Chart(ctx, {
      type: 'bar',
      data: chartData,
      options: { ...defaultOptions, ...options }
    });

    this.charts.set(canvasId, chart);
    return chart;
  }

  // Create a production vs sales chart (migrated from AngularJS)
  createProdSalesChart(
    canvasId: string,
    prefix: string,
    colorProd: string,
    colorSales: string,
    data: any
  ): Chart {
    const ctx = document.getElementById(canvasId) as HTMLCanvasElement;
    if (!ctx) {
      throw new Error(`Canvas element with id '${canvasId}' not found`);
    }

    // Destroy existing chart if it exists
    if (this.charts.has(canvasId)) {
      this.charts.get(canvasId)?.destroy();
    }

    const chartData: ChartData = {
      labels: data.dates || [],
      datasets: [
        {
          label: `${prefix} Production`,
          data: data.production || [],
          borderColor: colorProd,
          backgroundColor: `${colorProd}20`,
          fill: true,
          tension: 0.4,
          type: 'line' as const
        },
        {
          label: `${prefix} Sales`,
          data: data.sales || [],
          borderColor: colorSales,
          backgroundColor: `${colorSales}20`,
          fill: true,
          tension: 0.4,
          type: 'line' as const
        }
      ]
    };

    const chart = new Chart(ctx, {
      type: 'line',
      data: chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top' as const
          },
          tooltip: {
            mode: 'index' as const,
            intersect: false
          }
        },
        scales: {
          x: {
            type: 'category' as const,
            display: true,
            title: {
              display: true,
              text: 'Date'
            }
          },
          y: {
            type: 'linear' as const,
            display: true,
            title: {
              display: true,
              text: 'Volume'
            }
          }
        }
      }
    });

    this.charts.set(canvasId, chart);
    return chart;
  }

  // Create a driver chart (migrated from AngularJS)
  createDriverChart(
    canvasId: string,
    data: any,
    options: Partial<ChartOptions> = {}
  ): Chart {
    const ctx = document.getElementById(canvasId) as HTMLCanvasElement;
    if (!ctx) {
      throw new Error(`Canvas element with id '${canvasId}' not found`);
    }

    // Destroy existing chart if it exists
    if (this.charts.has(canvasId)) {
      this.charts.get(canvasId)?.destroy();
    }

    const chartData: ChartData = {
      labels: data.labels || [],
      datasets: data.datasets || []
    };

    const defaultOptions: ChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'top' as const
        },
        tooltip: {
          mode: 'index' as const,
          intersect: false
        }
      },
      scales: {
        x: {
          type: 'category' as const,
          display: true
        },
        y: {
          type: 'linear' as const,
          display: true
        }
      }
    };

    const chart = new Chart(ctx, {
      type: 'line',
      data: chartData,
      options: { ...defaultOptions, ...options }
    });

    this.charts.set(canvasId, chart);
    return chart;
  }

  // Update chart data
  updateChart(canvasId: string, newData: ChartData): void {
    const chart = this.charts.get(canvasId);
    if (chart) {
      chart.data = newData;
      chart.update();
    }
  }

  // Destroy a specific chart
  destroyChart(canvasId: string): void {
    const chart = this.charts.get(canvasId);
    if (chart) {
      chart.destroy();
      this.charts.delete(canvasId);
    }
  }

  // Destroy all charts
  destroyAllCharts(): void {
    this.charts.forEach(chart => chart.destroy());
    this.charts.clear();
  }

  // Get chart instance
  getChart(canvasId: string): Chart | undefined {
    return this.charts.get(canvasId);
  }

  // Process chart data (migrated from AngularJS)
  processChartData(fairPrice: any, forecast: any): any {
    if (!fairPrice || !forecast) return null;

    const dates = [...new Set([
      ...(fairPrice.dates || []),
      ...(forecast.dates || [])
    ])].sort();

    return {
      labels: dates,
      datasets: [
        {
          label: 'Fair Price',
          data: dates.map(date => {
            const index = fairPrice.dates?.indexOf(date);
            return index !== -1 ? fairPrice.values[index] : null;
          }),
          borderColor: rsColors.purple,
          backgroundColor: `${rsColors.purple}20`,
          fill: false,
          tension: 0.4
        },
        {
          label: 'Forecast',
          data: dates.map(date => {
            const index = forecast.dates?.indexOf(date);
            return index !== -1 ? forecast.values[index] : null;
          }),
          borderColor: rsColors.red,
          backgroundColor: `${rsColors.red}20`,
          fill: false,
          tension: 0.4,
          borderDash: [5, 5]
        }
      ]
    };
  }

  // Process commentary data (migrated from AngularJS)
  processCommentaryData(allCommentary: any[], polymerKey: string): any {
    if (!allCommentary || !polymerKey) return [];

    return allCommentary
      .filter(commentary => commentary.polymer === polymerKey)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .map(commentary => ({
        ...commentary,
        formattedText: commentary.text.replace(/\n/g, '<br>').replace(/  /g, '&nbsp;&nbsp;')
      }));
  }
}

// Export singleton instance
export const chartService = new ChartService();

// Export utility functions
export const renderCharts = (_chartData: any): void => {
  // This function can be used to render multiple charts at once
  // Implementation depends on specific chart data structure
};

export const convertValue = (convert: boolean, price: number): number => {
  if (!convert) return price;
  return price * 1000; // Convert to per ton if needed
};
